import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { 
  PlayCircleIcon,
  DocumentTextIcon,
  AcademicCapIcon,
  ClockIcon,
  CheckCircleIcon,
  UserGroupIcon,
  BookOpenIcon
} from '@heroicons/react/24/outline';

const TrainingPage = () => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('videos');

  const videoTutorials = [
    {
      id: 1,
      title: "FBRSTAPP Complete Overview",
      description: "Get a comprehensive introduction to all FBRSTAPP features and capabilities",
      duration: "15:30",
      level: "Beginner",
      thumbnail: "/api/placeholder/400/225",
      completed: false
    },
    {
      id: 2,
      title: "Setting Up Your Account",
      description: "Learn how to configure your account and connect to FBR API",
      duration: "8:45",
      level: "Beginner",
      thumbnail: "/api/placeholder/400/225",
      completed: true
    },
    {
      id: 3,
      title: "Creating Your First Invoice",
      description: "Step-by-step guide to creating and submitting your first invoice",
      duration: "12:20",
      level: "Beginner",
      thumbnail: "/api/placeholder/400/225",
      completed: true
    },
    {
      id: 4,
      title: "Advanced Invoice Features",
      description: "Explore advanced features like bulk operations and custom templates",
      duration: "18:15",
      level: "Intermediate",
      thumbnail: "/api/placeholder/400/225",
      completed: false
    },
    {
      id: 5,
      title: "FBR API Integration Deep Dive",
      description: "Understanding FBR API responses, error handling, and troubleshooting",
      duration: "22:30",
      level: "Advanced",
      thumbnail: "/api/placeholder/400/225",
      completed: false
    },
    {
      id: 6,
      title: "Team Management & Permissions",
      description: "How to manage team members and set up proper access controls",
      duration: "10:45",
      level: "Intermediate",
      thumbnail: "/api/placeholder/400/225",
      completed: false
    }
  ];

  const guides = [
    {
      title: "Quick Start Guide",
      description: "Get up and running with FBRSTAPP in 10 minutes",
      readTime: "10 min read",
      category: "Getting Started"
    },
    {
      title: "FBR Compliance Checklist",
      description: "Ensure your invoices meet all FBR requirements",
      readTime: "5 min read",
      category: "Compliance"
    },
    {
      title: "Troubleshooting Common Issues",
      description: "Solutions to the most frequently encountered problems",
      readTime: "15 min read",
      category: "Support"
    },
    {
      title: "API Integration Best Practices",
      description: "Tips for optimal FBR API integration and performance",
      readTime: "12 min read",
      category: "Technical"
    },
    {
      title: "Security & Data Protection",
      description: "Understanding how your data is protected in FBRSTAPP",
      readTime: "8 min read",
      category: "Security"
    },
    {
      title: "Billing & Subscription Management",
      description: "Managing your subscription and understanding billing",
      readTime: "6 min read",
      category: "Account"
    }
  ];

  const webinars = [
    {
      title: "FBRSTAPP Masterclass: Advanced Features",
      date: "December 15, 2024",
      time: "2:00 PM PKT",
      duration: "60 minutes",
      instructor: "Ahmed Khan, Product Expert",
      status: "upcoming"
    },
    {
      title: "FBR Compliance Updates & Changes",
      date: "December 8, 2024",
      time: "3:00 PM PKT",
      duration: "45 minutes",
      instructor: "Sarah Ali, Compliance Specialist",
      status: "completed"
    },
    {
      title: "Getting Started with FBRSTAPP",
      date: "December 1, 2024",
      time: "11:00 AM PKT",
      duration: "30 minutes",
      instructor: "Muhammad Hassan, Customer Success",
      status: "completed"
    }
  ];

  const getLevelColor = (level) => {
    switch (level) {
      case 'Beginner': return 'bg-green-100 text-green-800';
      case 'Intermediate': return 'bg-yellow-100 text-yellow-800';
      case 'Advanced': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <Link to="/home" className="flex items-center">
              <img 
                src="/fbrlogo1.svg" 
                alt="FBR Logo" 
                className="h-10 w-10 mr-3"
                onError={(e) => {
                  e.target.style.display = 'none';
                }}
              />
              <div>
                <h1 className="text-2xl font-bold text-blue-600">FBRSTAPP</h1>
                <p className="text-sm text-gray-600">Training Center</p>
              </div>
            </Link>
            <div className="flex items-center gap-3">
              {user ? (
                <Link to="/app/dashboard" className="px-4 py-2 text-blue-600 hover:text-blue-700 font-medium">Dashboard</Link>
              ) : (
                <>
                  <Link to="/login" className="px-4 py-2 text-blue-600 hover:text-blue-700 font-medium">Sign In</Link>
                  <Link to="/register" className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium">Get Started</Link>
                </>
              )}
              <Link
                to="/home"
                className="px-4 py-2 text-blue-600 hover:text-blue-700 font-medium"
              >
                ← Back to Home
              </Link>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="bg-blue-600 text-white py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <AcademicCapIcon className="h-16 w-16 mx-auto mb-6" />
          <h1 className="text-4xl font-bold mb-4">Learn FBRSTAPP</h1>
          <p className="text-xl text-blue-100">
            Master FBR digital invoicing with our comprehensive training resources
          </p>
        </div>
      </section>

      {/* Progress Overview */}
      <section className="py-8 bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            <div className="text-center">
              <div className="text-2xl font-bold text-blue-600">6</div>
              <div className="text-sm text-gray-600">Video Tutorials</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-green-600">2</div>
              <div className="text-sm text-gray-600">Completed</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-purple-600">6</div>
              <div className="text-sm text-gray-600">Written Guides</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-orange-600">3</div>
              <div className="text-sm text-gray-600">Live Webinars</div>
            </div>
          </div>
        </div>
      </section>

      {/* Training Content */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          
          {/* Tabs */}
          <div className="flex space-x-1 bg-gray-100 p-1 rounded-lg mb-8 max-w-md mx-auto">
            <button
              onClick={() => setActiveTab('videos')}
              className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${
                activeTab === 'videos'
                  ? 'bg-white text-blue-600 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <PlayCircleIcon className="h-4 w-4 inline mr-1" />
              Videos
            </button>
            <button
              onClick={() => setActiveTab('guides')}
              className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${
                activeTab === 'guides'
                  ? 'bg-white text-blue-600 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <DocumentTextIcon className="h-4 w-4 inline mr-1" />
              Guides
            </button>
            <button
              onClick={() => setActiveTab('webinars')}
              className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${
                activeTab === 'webinars'
                  ? 'bg-white text-blue-600 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <UserGroupIcon className="h-4 w-4 inline mr-1" />
              Webinars
            </button>
          </div>

          {/* Video Tutorials */}
          {activeTab === 'videos' && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {videoTutorials.map((video) => (
                <div key={video.id} className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow">
                  <div className="relative">
                    <div className="w-full h-48 bg-gray-200 flex items-center justify-center">
                      <PlayCircleIcon className="h-16 w-16 text-blue-600" />
                    </div>
                    <div className="absolute bottom-2 right-2 bg-black bg-opacity-75 text-white px-2 py-1 rounded text-sm">
                      {video.duration}
                    </div>
                    {video.completed && (
                      <div className="absolute top-2 left-2 bg-green-500 text-white p-1 rounded-full">
                        <CheckCircleIcon className="h-4 w-4" />
                      </div>
                    )}
                  </div>
                  <div className="p-6">
                    <div className="flex items-center justify-between mb-2">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${getLevelColor(video.level)}`}>
                        {video.level}
                      </span>
                      <div className="flex items-center text-gray-500 text-sm">
                        <ClockIcon className="h-4 w-4 mr-1" />
                        {video.duration}
                      </div>
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">{video.title}</h3>
                    <p className="text-gray-600 text-sm mb-4">{video.description}</p>
                    <button className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium">
                      {video.completed ? 'Watch Again' : 'Start Learning'}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Written Guides */}
          {activeTab === 'guides' && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {guides.map((guide, index) => (
                <div key={index} className="bg-white rounded-xl shadow-lg p-6 hover:shadow-xl transition-shadow">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center">
                      <BookOpenIcon className="h-6 w-6 text-blue-600 mr-3" />
                      <span className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm font-medium">
                        {guide.category}
                      </span>
                    </div>
                    <div className="flex items-center text-gray-500 text-sm">
                      <ClockIcon className="h-4 w-4 mr-1" />
                      {guide.readTime}
                    </div>
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">{guide.title}</h3>
                  <p className="text-gray-600 mb-4">{guide.description}</p>
                  <button className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium">
                    Read Guide
                  </button>
                </div>
              ))}
            </div>
          )}

          {/* Webinars */}
          {activeTab === 'webinars' && (
            <div className="space-y-6">
              {webinars.map((webinar, index) => (
                <div key={index} className="bg-white rounded-xl shadow-lg p-6 hover:shadow-xl transition-shadow">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center mb-2">
                        <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                          webinar.status === 'upcoming' 
                            ? 'bg-green-100 text-green-800' 
                            : 'bg-gray-100 text-gray-800'
                        }`}>
                          {webinar.status === 'upcoming' ? 'Upcoming' : 'Completed'}
                        </span>
                      </div>
                      <h3 className="text-xl font-semibold text-gray-900 mb-2">{webinar.title}</h3>
                      <div className="flex items-center text-gray-600 text-sm space-x-4 mb-2">
                        <span>{webinar.date}</span>
                        <span>{webinar.time}</span>
                        <span>{webinar.duration}</span>
                      </div>
                      <p className="text-gray-600">Instructor: {webinar.instructor}</p>
                    </div>
                    <div className="ml-6">
                      <button className={`px-6 py-2 rounded-lg font-medium ${
                        webinar.status === 'upcoming'
                          ? 'bg-blue-600 text-white hover:bg-blue-700'
                          : 'bg-gray-200 text-gray-800 hover:bg-gray-300'
                      }`}>
                        {webinar.status === 'upcoming' ? 'Register' : 'Watch Recording'}
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

        </div>
      </section>

      {/* Need More Help */}
      <section className="py-16 bg-blue-600 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold mb-4">Need Personalized Training?</h2>
          <p className="text-xl text-blue-100 mb-8">
            Our team can provide customized training sessions for your organization
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/contact-us"
              className="px-8 py-3 bg-white text-blue-600 rounded-lg hover:bg-gray-100 font-medium"
            >
              Request Custom Training
            </Link>
            <Link
              to="/support"
              className="px-8 py-3 border-2 border-white text-white rounded-lg hover:bg-blue-700 font-medium"
            >
              Get Support
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default TrainingPage;